import "reactflow/dist/style.css";
import "./index.css";
import "./node-card.css";
import "./topbar.css";
import "./controls.css";
import "./modal-reskin.css";
import React, {
	useState,
	useRef,
	useCallback,
	useEffect,
	useContext,
	useMemo,
} from "react";
import { toast, ToastContainer } from "react-toastify";
import ReactFlow, {
	useNodesState,
	useEdgesState,
	addEdge,
	ReactFlowProvider,
	Controls,
	ControlButton,
	MiniMap,
	Background,
	MarkerType,
} from "reactflow";
import { MdBarChart, MdClose, MdMoreVert, MdSettings } from "react-icons/md";
// FaSitemap: reativar junto com o botão "Auto-organizar" (oculto por enquanto)
import { FaMoon, FaSun, FaExpandArrowsAlt } from "react-icons/fa";

import Sidebar from "./Sidebar";
import Condition from "../Nodes/Condition";
import Start from "../Nodes/Start";
import SendMessage from "../Nodes/SendMessage";
import Webhook from "../Nodes/Webhook";
import Question from "../Nodes/Question";
import AiAgent, { AI_CONTINGENCY_HANDLE } from "../Nodes/AiAgent";
import CreateUpdateContact from "../Nodes/CreateUpdateContact";
import End from "../Nodes/End";
import Gush from "../Nodes/Gush";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { serverApi } from "../../services/api";
import { generateId } from "../../services/generateId";
import SaveButton from "../Elements/SaveButton";
import Modal from "../Elements/ModalEdit/Modal";
import Jump from "../Nodes/Jump";
import SetVar from "../Nodes/SetVar";
import Sleep from "../Nodes/Sleep";
import ButtonEdge from "../Elements/ButtonEdge";
import CreateTask from "../Nodes/CreateTask";
import CreateDeal from "../Nodes/CreateDeal";
import CreateTicket from "../Nodes/CreateTicket";
import { defaultVariables } from "../../helpers/default-variables";
import { buildBuilderQueryString } from "../../helpers/build-builder-query";
import ModalVariable from "../Elements/ModalVariable";
import SetTag from "../Nodes/SetTag";
import { SettingsContainer } from "../Elements/ModalEdit/Settings";
import { AccountContext } from "../../context/Account";
import Comment from "../Nodes/Comment";
import OutputBalancer from "../Nodes/OutputBalancer";
import FullScreenLoader from "../Elements/Loading";
import WppFlows, { WPP_FLOWS_DEFAULT_HANDLE } from "../Nodes/WppFlows";
import { useUndoRedo } from "./useUndoRedo";
import NodeSearch from "./NodeSearch";
import ShortcutsHint from "./ShortcutsHint";
import WhatsNew from "./WhatsNew";
import LayoutFixModal from "./LayoutFixModal";

const nodeTypes = {
	conditional: Condition,
	start: Start,
	message: SendMessage,
	webhook: Webhook,
	aiagent: AiAgent,
	question: Question,
	createupdatecontact: CreateUpdateContact,
	end: End,
	gush: Gush,
	jump: Jump,
	setvar: SetVar,
	settag: SetTag,
	sleep: Sleep,
	createtask: CreateTask,
	createdeal: CreateDeal,
	createticket: CreateTicket,
	createnote: Comment,
	outputbalancer: OutputBalancer,
	wppFlows: WppFlows,
};

const edgeTypes = {
	buttonedge: ButtonEdge,
};

// Cor da seta (marker) = cor da linha. Saídas negativas (condicional "false",
// erro de webhook) são rosa; o restante é verde — igual às regras do index.css.
const NEGATIVE_HANDLES = ["false", "web-error", AI_CONTINGENCY_HANDLE];
function edgeBranchColor(sourceHandle) {
	const handle = String(sourceHandle || "");
	const negative = NEGATIVE_HANDLES.some((h) => handle.includes(h));
	return negative ? "#d04881" : "#3ccfa0";
}

let canOpenModal = true;

// Detecta layout "quebrado": pares de cards realmente empilhados (ticket 14).
// Usa as dimensões medidas pelo reactflow (node.width/height). Um "empilhamento"
// = sobreposição de >MIN_OVERLAP px em AMBOS os eixos (não só encostar numa
// borda). Área não serve bem: cards altos (muito texto) se sobrepõem muito mas
// a interseção fica pequena vs. a área total. Bots já feitos no card novo ficam
// com 0 empilhamentos, então basta 1 par para considerar quebrado.
function hasBrokenLayout(nodes) {
	const boxes = nodes.filter(
		(n) => n.width && n.height && n.position && Number.isFinite(n.position.x)
	);
	if (boxes.length < 2) return false;
	// Sensível: bots bem arranjados têm 0 sobreposição, então basta um encosto
	// real (>8px em ambos os eixos) pra considerar quebrado.
	const MIN_OVERLAP = 8; // px em ambos os eixos
	for (let i = 0; i < boxes.length; i++) {
		for (let j = i + 1; j < boxes.length; j++) {
			const a = boxes[i];
			const b = boxes[j];
			const ix =
				Math.min(a.position.x + a.width, b.position.x + b.width) -
				Math.max(a.position.x, b.position.x);
			const iy =
				Math.min(a.position.y + a.height, b.position.y + b.height) -
				Math.max(a.position.y, b.position.y);
			if (ix > MIN_OVERLAP && iy > MIN_OVERLAP) return true;
		}
	}
	return false;
}

const Flow = () => {
	const { botId } = useParams();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const isRvopsFrame = searchParams.get("isRvopsFrame") === "true";
	const { authUser, account } = useContext(AccountContext);
	const reactFlowWrapper = useRef(null);
	const edgeUpdateSuccessful = useRef(true);
	const [contactConditions, setContactConditions] = useState([]);
	const [nodes, setNodes, onNodesChange] = useNodesState([]);
	const [edges, setEdges, onEdgesChange] = useEdgesState([]);
	const [reactFlowInstance, setReactFlowInstance] = useState(null);
	// Modal reativo que oferece "Espalhar" quando detecta layout sobreposto ao
	// abrir o builder (ticket 14). Só checa 1× por carregamento de fluxo.
	const [layoutFixOpen, setLayoutFixOpen] = useState(false);
	const layoutCheckedRef = useRef(false);
	const reactFlowInstanceRef = useRef(null);
	const [nodeToEdit, setNodeToEdit] = useState({});
	const [stateModalEdit, setStateModalEdit] = useState(false);
	const [variables, setVariables] = useState([]);
	const [botName, setBotName] = useState("");
	const [botSettings, setBotSettings] = useState({});
	const [owners, setOwners] = useState([]);
	const [operators, setOperators] = useState([]);
	const [dealPipelines, setDealPipelines] = useState([]);
	const [customDealFields, setCustomDealFields] = useState([]);
	const [ticketPipelines, setTicketPipelines] = useState([]);
	const [customTicketFields, setCustomTicketFields] = useState([]);
	const [contactFields, setContactFields] = useState([]);
	const [sectors, setSectors] = useState([]);
	const [chatTags, setChatTags] = useState([]);
	const [agents, setAgents] = useState([]);
	const [isSettingsModalOpen, setSettingsModalOpen] = useState(false);
	const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	// Só vira true quando o fluxo do backend foi carregado no canvas. Antes
	// disso `nodes` é [] e um save sobrescreveria o bot inteiro com nada.
	const [flowLoaded, setFlowLoaded] = useState(false);
	const [canvasDark, setCanvasDark] = useState(() => {
		try {
			return localStorage.getItem("builderCanvasDark") === "1";
		} catch {
			return false;
		}
	});

	const toggleCanvasTheme = () => {
		setCanvasDark((dark) => {
			const next = !dark;
			try {
				localStorage.setItem("builderCanvasDark", next ? "1" : "0");
			} catch {}
			return next;
		});
	};

	const [modalActive, setModalActive] = useState("");
	const [editOpen, setEditOpen] = useState(false);
	const [nameVar, setNameVar] = useState("");
	const [elozProperty, setElozProperty] = useState("");

	// Histórico undo/redo (Ctrl+Z / Ctrl+Y) — ver useUndoRedo.js e ticket 01.
	// takeSnapshot() é chamado ANTES de cada ação que muta nodes/edges.
	const { takeSnapshot, resetHistory } = useUndoRedo({
		nodes,
		edges,
		setNodes,
		setEdges,
		isEditing: stateModalEdit,
	});

	const handleModalVariable = () => {
		setModalActive(!modalActive);
		setNameVar("");
	};

	function handleEditVariable() {
		setEditOpen((isOpen) => !isOpen);
	}

	const getVars = async () => {
		const resp = await serverApi.get(`/bot/variable/${botId}`);
		let respVars = resp.data;
		respVars.sort((a, b) => (defaultVariables.includes(a.name) ? -1 : 1));
		setVariables(resp.data);
	};

	const getSectors = async () => {
		try {
			const resp = await serverApi.get(`/sectors/${botId}`);
			setSectors(resp.data);
		} catch (e) {
			console.log(e);
			setSectors([]);
		}
	};

	const handleCustomVariables = async () => {
		const { data } = await serverApi.get("/bot/custom-variables");
		setContactConditions(data);
	};

	const getChatTags = async () => {
		try {
			const resp = await serverApi.get(`/tags/${botId}`);
			setChatTags(resp.data);
			return resp.data;
		} catch (e) {
			console.log(e);
			setChatTags([]);
			return [];
		}
	};

	const getAgents = async () => {
		try {
			const resp = await serverApi.get(`/bot/ai/agents/${botId}`);
			setAgents(resp.data || []);
			return resp.data || [];
		} catch (e) {
			console.log(e);
			setAgents([]);
			return [];
		}
	};

	const saveVar = async (e) => {
		if (!nameVar) {
			alert("Preencha todos os campos");
			return;
		}
		e.target.setAttribute("disabled", true);
		await serverApi.post(`/bot/variable/${botId}`, {
			name: nameVar,
			elozProperty,
		});
		e.target.removeAttribute("disabled");
		getVars();
		handleModalVariable();
	};

	async function handleEditVar(e, varId, elozProperty, name) {
		e.target.setAttribute("disabled", true);
		await serverApi.patch(`/bot/variable/${botId}/${varId}`, {
			elozProperty,
			name,
		});
		e.target.removeAttribute("disabled");
		getVars();
		handleEditVariable();
	}

	const getOperators = async () => {
		try {
			const resp = await serverApi.get(`/operators/${botId}`);
			setOperators(resp.data);
			return resp.data;
		} catch (e) {
			console.log(e);
			setOperators([]);
		}
	};

	// Retorna true se salvou. Nunca envia um fluxo vazio/sem start.
	const saveBot = async (e) => {
		if (!flowLoaded) {
			toast.warn("Aguarde o fluxo terminar de carregar antes de salvar.");
			return false;
		}
		if (!nodes.some((node) => node.type === "start")) {
			alert(
				"O fluxo está sem o bloco de início. Recarregue a página antes de salvar."
			);
			return false;
		}
		try {
			e.target.setAttribute("disabled", true);
			const formData = new FormData();

			formData.append("botId", botId);
			formData.append("edges", JSON.stringify(edges));
			formData.append("user", JSON.stringify(authUser));

			const nodesSerialized = nodes.map((node, index) => {
				const nodeCopia = { ...node };
				if (node.data.msgMedia?.file) {
					nodeCopia.data.msgMedia.fileField = `file_${index}`;
					delete nodeCopia.file;
				}
				return nodeCopia;
			});
			formData.append("nodes", JSON.stringify(nodesSerialized));

			nodes.forEach((node, index) => {
				if (node.data.msgMedia?.file) {
					formData.append(
						`file_${index}`,
						node.data.msgMedia?.file,
						node.data.msgMedia.name
					);
				}
			});
			setLoading(true);
			await serverApi.post("/bot/save", formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});
			setLoading(false);
			toast.success("Bot salvo com sucesso", 5_000);
			e.target.removeAttribute("disabled");
			return true;
		} catch (err) {
			setLoading(false);
			e.target.removeAttribute("disabled");
			const reason = err?.response?.data?.error;
			alert(reason ? `Erro ao salvar bot: ${reason}` : "Erro ao salvar bot");
			console.log(err);
			return false;
		}
	};

	const handleCustomDealFields = async (botId) => {
		let { data } = await serverApi.get(`/bot/eloz/fields/deal/custom/${botId}`);
		data = data.filter(
			(p) => !["contacts", "createdAt", "updatedAt"].includes(p.alias)
		);
		setCustomDealFields(data);
	};

	const handleCustomTicketFields = async (botId) => {
		let { data } = await serverApi.get(
			`/bot/eloz/fields/ticket/custom/${botId}`
		);
		data = data.filter(
			(p) => !["contacts", "createdAt", "updatedAt"].includes(p.alias)
		);
		setCustomTicketFields(data);
	};

	const handleContactFields = async (botId) => {
		const { data } = await serverApi.get(`/bot/eloz/fields/contact/${botId}`);
		setContactFields(data);
	};

	const handleGetElozOwnersByAccount = async (botId) => {
		const response = await serverApi.get(`/bot/eloz/owners/${botId}`);
		const owners = response.data;
		setOwners(owners);
	};

	const handleGetElozDealsPipelinesByAccount = async (botId) => {
		const response = await serverApi.get(`/bot/eloz/deal/pipelines/${botId}`);
		const pipelines = response.data;
		setDealPipelines(pipelines);
	};

	const handleGetElozTicketsPipelinesByAccount = async (botId) => {
		const response = await serverApi.get(`/bot/eloz/ticket/pipelines/${botId}`);
		const pipelines = response.data;
		setTicketPipelines(pipelines);
	};

	const getNodes = async (botId, agentsList = null) => {
		const [operators, chattags, agentsData, { data }] = await Promise.all([
			getOperators(),
			getChatTags(),
			agentsList || getAgents(),
			serverApi.get(`/bot/${botId}`),
		]);

		const nodesArr = data.nodes.map((node) => {
			// Popular aiAgentName se o nó for do tipo aiagent e tiver aiAgentId
			let nodeData = { ...node };
			if (node.type === "aiagent" && node.aiAgentId && !node.aiAgentName) {
				const foundAgent = agentsData.find(
					(a) => a?.id === node.aiAgentId || a?._id === node.aiAgentId
				);
				if (foundAgent) {
					nodeData.aiAgentName = foundAgent.name;
				}
			}

			return {
				id: node.ref,
				...(node.type == "conditional" ? { type: "conditional" } : {}),
				...(node.type == "start" ? { type: "start" } : {}),
				...(node.type == "message" ? { type: "message" } : {}),
				...(node.type == "webhook" ? { type: "webhook" } : {}),
				...(node.type == "aiagent" ? { type: "aiagent" } : {}),
				...(node.type == "createupdatecontact"
					? { type: "createupdatecontact" }
					: {}),
				...(node.type == "question" ? { type: "question" } : {}),
				...(node.type == "wppFlows" ? { type: "wppFlows" } : {}),
				...(node.type == "end" ? { type: "end" } : {}),
				...(node.type == "gush" ? { type: "gush" } : {}),
				...(node.type == "jump" ? { type: "jump" } : {}),
				...(node.type == "setvar" ? { type: "setvar" } : {}),
				...(node.type == "settag" ? { type: "settag" } : {}),
				...(node.type == "sleep" ? { type: "sleep" } : {}),
				...(node.type == "createtask" ? { type: "createtask" } : {}),
				...(node.type == "createdeal" ? { type: "createdeal" } : {}),
				...(node.type == "createticket" ? { type: "createticket" } : {}),
				...(node.type == "createnote" ? { type: "createnote" } : {}),
				...(node.type == "outputbalancer" ? { type: "outputbalancer" } : {}),
				sourcePosition: "right",
				targetPosition: "left",
				data: {
					...nodeData,
					botId,
					deleteNodeById,
					openModalEdit,
					currentNode: node,
					setNodes,
					takeSnapshot,
					operators,
					chattags,
					agents: agentsData,
				},
				position: node.position,
			};
		});

		// Blindagem contra edges duplicados vindos do backend: alguns bots têm a
		// MESMA conexão (source/target/handles iguais) repetida dezenas de vezes,
		// todas com o MESMO `id`. Como o reactflow usa o id como key do React,
		// ids repetidos colidem e, a cada re-render (mover card, selecionar), o
		// DOM dos edges é duplicado/acumulado e os fios "escadeiam" (bug visual +
		// vazamento crescente de DOM). Aqui mantemos uma conexão por tupla e
		// garantimos id único por edge.
		const edgeSeen = new Set();
		const edgeIds = new Set();
		const edgesArr = (data.edges || []).reduce((acc, edge) => {
			const sh = edge.sourceHandle || "";
			const th = edge.targetHandle || "";
			const connKey = `${edge.source}|${sh}|${edge.target}|${th}`;
			if (edgeSeen.has(connKey)) return acc; // conexão duplicada → descarta
			edgeSeen.add(connKey);

			let id = edge.id;
			if (!id || edgeIds.has(id)) {
				id = `${edge.source}-${sh}-${edge.target}-${th}-${acc.length}`;
			}
			edgeIds.add(id);

			acc.push({
				id,
				source: edge.source,
				target: edge.target,
				type: "buttonedge",
				sourceHandle: edge.sourceHandle || undefined,
				markerEnd: {
					type: MarkerType.ArrowClosed,
					color: edgeBranchColor(edge.sourceHandle),
				},
				data: {
					setEdges,
				},
			});
			return acc;
		}, []);

		setNodes(nodesArr);
		setEdges(edgesArr);
		setFlowLoaded(true);
		setBotName(data.name);
		setBotSettings(data.settings);
		// Fluxo recém-carregado é o baseline: histórico começa limpo.
		resetHistory();
		// Permite uma nova checagem de sobreposição para este fluxo e agenda-a
		// (dá tempo do reactflow renderizar/medir os cards).
		layoutCheckedRef.current = false;
		setTimeout(() => runLayoutCheck(0), 700);
	};

	const handleModalEdit = () => {
		setStateModalEdit(!stateModalEdit);
	};

	const openModalEdit = (data) => {
		if (!stateModalEdit) {
			setNodeToEdit(data.data);
		}
		setStateModalEdit(!stateModalEdit);
		handleModalEdit();
	};

	const saveNode = (data) => {
		takeSnapshot();
		const x = nodes.map((node) => {
			if (data.question && data.question.includes("delay")) {
				data.question = data.question.replace("&lt;delay&gt;", "<delay>");
				data.question = data.question.replace("&lt;/delay&gt;", "</delay>");
			}

			if (data.msgMessage && data.msgMessage.includes("delay")) {
				data.msgMessage = data.msgMessage.replace("&lt;delay&gt;", "<delay>");
				data.msgMessage = data.msgMessage.replace("&lt;/delay&gt;", "</delay>");
			}

			if (node.id === data.ref) {
				node.data = {
					...node.data,
					...data,
				};
			}
			return node;
		});
		setNodes(x);

		// Se for um nó do tipo aiagent, remover conexões que não correspondem mais às saídas válidas
		if (data.type === "aiagent" && data.aiOutputs) {
			const validOutputIds = [
				...data.aiOutputs.map((output) => output.name),
				AI_CONTINGENCY_HANDLE,
			];

			setEdges((currentEdges) =>
				currentEdges.filter((edge) => {
					// Se a conexão sai do nó que foi editado
					if (edge.source === data.ref) {
						// Verificar se o sourceHandle ainda existe nos outputs válidos
						return validOutputIds.includes(edge.sourceHandle);
					}
					// Manter todas as outras conexões
					return true;
				})
			);
		}

		// Flow obrigatório não tem a saída "Padrão": remove as conexões dela
		if (data.wppFlows?.required) {
			setEdges((currentEdges) =>
				currentEdges.filter(
					(edge) =>
						edge.source !== data.ref ||
						edge.sourceHandle !== WPP_FLOWS_DEFAULT_HANDLE
				)
			);
		}

		handleModalEdit();
	};

	const deleteNodeById = (id) => {
		takeSnapshot();
		setNodes((nds) => nds.filter((node) => node.id !== id));
	};

	const onConnect = useCallback((params) => {
		canOpenModal = false;
		takeSnapshot();
		setEdges((eds) =>
			addEdge(
				{
					...params,
					type: "buttonedge",
					markerEnd: {
						type: MarkerType.ArrowClosed,
						color: edgeBranchColor(params.sourceHandle),
					},
					data: { setEdges },
				},
				eds
			)
		);
	}, [takeSnapshot, setEdges]);

	const onDragOver = useCallback((event) => {
		event.preventDefault();
		event.dataTransfer.dropEffect = "move";
	}, []);

	const onEdgeUpdateEnd = useCallback(
		(_, edge) => {
			if (!edgeUpdateSuccessful.current) {
				takeSnapshot();
				setEdges((eds) => eds.filter((e) => e.id !== edge.id));
			}

			edgeUpdateSuccessful.current = true;
		},
		[takeSnapshot, setEdges]
	);

	const onDrop = useCallback(
		(event) => {
			event.preventDefault();
			const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
			const type = event.dataTransfer.getData("application/reactflow");

			// check if the dropped element is valid
			if (typeof type === "undefined" || !type) {
				return;
			}

			takeSnapshot();

			const position = reactFlowInstance.project({
				x: event.clientX - reactFlowBounds.left,
				y: event.clientY - reactFlowBounds.top,
			});

			let newNode;
			const nodeId = generateId();

			newNode = {
				id: nodeId,
				type,
				position,
				sourcePosition: "right",
				targetPosition: "left",
				data: {
					position,
					botId,
					ref: nodeId,
					type,
					deleteNodeById,
					openModalEdit,
					setNodes,
					takeSnapshot,
					operators,
					chattags: chatTags,
					agents,
				},
			};

			setNodes((nds) => nds.concat(newNode));
		},
		[reactFlowInstance, operators, chatTags, takeSnapshot]
	);

	useEffect(() => {
		getNodes(botId).catch((err) => {
			console.log(err);
			toast.error(
				"Erro ao carregar o fluxo. Recarregue a página antes de editar.",
				{ autoClose: false }
			);
		});
		handleGetElozOwnersByAccount(botId);
		handleGetElozDealsPipelinesByAccount(botId);
		handleCustomDealFields(botId);
		handleGetElozTicketsPipelinesByAccount(botId);
		handleCustomTicketFields(botId);
		handleContactFields(botId);
		handleCustomVariables();
		getVars();
		getSectors();
	}, []);

	const handleMouseEvent = (style) => {
		return (e) => {
			const id =
				e?.target?.parentElement?.dataset?.testid?.split("rf__edge-")[1];
			if (!id) return;
			document.querySelector(`[data-line-action="${id}"]`).style.display =
				style;
		};
	};

	// Destaque dos fios por hover (print #19/#20): por padrão os edges ficam
	// ATRÁS dos cards (canvas limpo). Ao passar o mouse num nó, elevamos só os
	// fios dele (zIndex alto → camada acima dos nós no reactflow), pra enxergar
	// de qual saída cada fio parte sem poluir o resto. É só visual: não altera o
	// estado salvo dos edges (zIndex é derivado, não persistido).
	const [hoveredNodeId, setHoveredNodeId] = useState(null);
	// Grace period ao limpar o hover (print #22, ponto 1): sem isso, ao mover o
	// mouse para cima de um fio elevado que cruza o próprio card de origem, o fio
	// (no topo) "rouba" o hover do card → leave/enter em loop → o fio pisca/some.
	// O atraso evita o flicker e dá tempo de o mouse chegar no fio.
	const hoverClearTimer = useRef(null);
	const setHoverNow = useCallback((id) => {
		if (hoverClearTimer.current) {
			clearTimeout(hoverClearTimer.current);
			hoverClearTimer.current = null;
		}
		setHoveredNodeId(id);
	}, []);
	const clearHoverSoon = useCallback(() => {
		if (hoverClearTimer.current) clearTimeout(hoverClearTimer.current);
		hoverClearTimer.current = setTimeout(() => setHoveredNodeId(null), 150);
	}, []);

	const onNodeMouseEnter = useCallback(
		(_, node) => setHoverNow(node?.id ?? null),
		[setHoverNow]
	);
	const onNodeMouseLeave = useCallback(() => clearHoverSoon(), [clearHoverSoon]);

	// Passar o mouse no próprio fio mantém o destaque (segue o fio mesmo quando
	// ele cruza outros cards) + revela a lixeira da linha (comportamento atual).
	const onEdgeMouseEnter = useCallback(
		(e, edge) => {
			handleMouseEvent("unset")(e);
			setHoverNow(edge?.source ?? null);
		},
		[setHoverNow]
	);
	const onEdgeMouseLeave = useCallback(
		(e) => {
			handleMouseEvent("none")(e);
			clearHoverSoon();
		},
		[clearHoverSoon]
	);

	const displayEdges = useMemo(() => {
		if (!hoveredNodeId) return edges;
		return edges.map((e) =>
			e.source === hoveredNodeId || e.target === hoveredNodeId
				? { ...e, zIndex: 1001 }
				: e
		);
	}, [edges, hoveredNodeId]);

	const handleKeyDown = (e) => {
		const id = e.target.parentElement.dataset.testid.split("rf__edge-")[1];
		takeSnapshot();
		setEdges((edges) => edges.filter((ed) => ed.id !== id));
	};

	function handleSettings() {
		setSettingsModalOpen((isOpen) => {
			return !isOpen;
		});
	}

	function handleCloseFlow() {
		window.parent.postMessage({ closeFlow: true }, "*");
	}

	const goToAnalytics = () => {
		navigate(
			`/builder/${botId}/analytics${buildBuilderQueryString(searchParams)}`
		);
	};

	// Busca de nós (ticket 02): centraliza a tela no nó com animação e dá um
	// "pulse" de destaque. reactFlowInstance vem do onInit do <ReactFlow>.
	const focusNodeById = (node) => {
		if (!node) return;
		const width = node.width || 170;
		const height = node.height || 40;
		const cx = (node.position?.x || 0) + width / 2;
		const cy = (node.position?.y || 0) + height / 2;
		if (reactFlowInstance?.setCenter) {
			reactFlowInstance.setCenter(cx, cy, { zoom: 1.4, duration: 800 });
		}
		const el = document.querySelector(
			`.react-flow__node[data-id="${node.id}"]`
		);
		if (el) {
			el.classList.remove("rv-node-pulse");
			void el.offsetWidth; // reinicia a animação
			el.classList.add("rv-node-pulse");
			setTimeout(() => el.classList.remove("rv-node-pulse"), 8000);
		}
	};

	// ---- Reorganização de layout (ticket 12) ----
	// Bots antigos foram arranjados à mão para o card antigo (menor). Com os
	// cards novos (260px, mais altos) os nós podem se sobrepor. Dois botões
	// on-demand resolvem, ambos com snapshot (undo) e save manual.

	const fitAfterLayout = () => {
		setTimeout(() => {
			if (reactFlowInstance?.fitView) {
				reactFlowInstance.fitView({ duration: 600, padding: 0.2 });
			}
		}, 60);
	};

	// "Espalhar": multiplica as distâncias entre os nós por um fator, ancorando
	// no canto superior-esquerdo do fluxo (não sai do lugar). Preserva o arranjo
	// manual — só abre espaço. Compõe se clicado de novo; undo desfaz.
	const spreadNodes = (factor = 1.5) => {
		takeSnapshot();
		setNodes((nds) => {
			if (!nds.length) return nds;
			const minX = Math.min(...nds.map((n) => n.position?.x || 0));
			const minY = Math.min(...nds.map((n) => n.position?.y || 0));
			return nds.map((n) => ({
				...n,
				position: {
					x: minX + ((n.position?.x || 0) - minX) * factor,
					y: minY + ((n.position?.y || 0) - minY) * factor,
				},
			}));
		});
		fitAfterLayout();
	};

	/* "Auto-organizar" OCULTO por enquanto (a pedido) — manter só o "Espalhar".
	   Reativar: descomentar este bloco, o <ControlButton> abaixo e o import FaSitemap.

	// "Auto-organizar": re-desenha o fluxo em camadas esquerda→direita conforme
	// as conexões (layering por caminho mais longo, Kahn). Descarta o arranjo
	// manual em troca de zero sobreposição. Sem dependência externa. Undo desfaz.
	const autoLayoutNodes = () => {
		takeSnapshot();
		setNodes((nds) => {
			if (!nds.length) return nds;
			const COL_W = 380; // largura do card (260) + folga horizontal
			const ROW_H = 240; // altura típica do card + folga vertical
			const ids = nds.map((n) => n.id);
			const outAdj = {};
			const indeg = {};
			ids.forEach((id) => {
				outAdj[id] = [];
				indeg[id] = 0;
			});
			edges.forEach((e) => {
				if (outAdj[e.source] && indeg[e.target] !== undefined) {
					outAdj[e.source].push(e.target);
					indeg[e.target] += 1;
				}
			});
			// layer = maior distância a partir de uma raiz (indegree 0)
			const layer = {};
			ids.forEach((id) => (layer[id] = 0));
			const work = { ...indeg };
			const queue = ids.filter((id) => work[id] === 0);
			while (queue.length) {
				const id = queue.shift();
				outAdj[id].forEach((t) => {
					if (layer[t] < layer[id] + 1) layer[t] = layer[id] + 1;
					work[t] -= 1;
					if (work[t] === 0) queue.push(t);
				});
			}
			// agrupa por camada, mantendo a ordem atual dentro de cada uma
			const byLayer = {};
			ids.forEach((id) => {
				(byLayer[layer[id]] = byLayer[layer[id]] || []).push(id);
			});
			const posById = {};
			Object.keys(byLayer)
				.map(Number)
				.sort((a, b) => a - b)
				.forEach((L) => {
					byLayer[L].forEach((id, i) => {
						posById[id] = { x: L * COL_W, y: i * ROW_H };
					});
				});
			return nds.map((n) => ({
				...n,
				position: posById[n.id] || n.position,
			}));
		});
		fitAfterLayout();
	};
	*/

	// Detecção reativa de layout sobreposto (ticket 14): depois que o fluxo
	// carrega, espera o reactflow medir os cards e checa 1× se há sobreposição
	// relevante; se houver, abre o modal oferecendo o "Espalhar". Não reabre
	// sozinho depois. Faz algumas tentativas até a maioria dos cards ter dimensão.
	const runLayoutCheck = (attempt = 0) => {
		if (layoutCheckedRef.current) return;
		const inst = reactFlowInstanceRef.current;
		const current = inst && inst.getNodes ? inst.getNodes() : [];
		const measured = current.filter((n) => n.width && n.height);
		const enough =
			current.length > 0 &&
			measured.length >= Math.ceil(current.length * 0.6);
		if (enough) {
			layoutCheckedRef.current = true;
			if (hasBrokenLayout(current)) setLayoutFixOpen(true);
			return;
		}
		if (attempt < 5) setTimeout(() => runLayoutCheck(attempt + 1), 500);
	};

	// ---- Copiar/Colar ações (Ctrl+C / Ctrl+V) ----
	const nodesRef = useRef(nodes);
	const edgesRef = useRef(edges);
	const modalOpenRef = useRef(stateModalEdit);
	nodesRef.current = nodes;
	edgesRef.current = edges;
	modalOpenRef.current = stateModalEdit;
	const clipboardRef = useRef({ nodes: [], edges: [] });

	const isTextEntryTarget = (el) => {
		if (!el) return false;
		const tag = el.tagName;
		return (
			tag === "INPUT" ||
			tag === "TEXTAREA" ||
			tag === "SELECT" ||
			el.isContentEditable
		);
	};

	const copySelection = () => {
		const selected = nodesRef.current.filter((n) => n.selected);
		if (!selected.length) return false;
		const ids = new Set(selected.map((n) => n.id));
		const innerEdges = edgesRef.current.filter(
			(e) => ids.has(e.source) && ids.has(e.target)
		);
		clipboardRef.current = {
			nodes: selected.map((n) => ({
				...n,
				data: { ...n.data },
				position: { ...n.position },
			})),
			edges: innerEdges.map((e) => ({ ...e })),
		};
		return true;
	};

	const pasteSelection = () => {
		const clip = clipboardRef.current;
		if (!clip.nodes.length) return false;
		takeSnapshot();
		const idMap = {};
		const offset = 48;
		const pastedNodes = clip.nodes.map((n) => {
			const newId = generateId();
			idMap[n.id] = newId;
			return {
				...n,
				id: newId,
				selected: true,
				position: {
					x: (n.position?.x || 0) + offset,
					y: (n.position?.y || 0) + offset,
				},
				data: { ...n.data, ref: newId },
			};
		});
		const pastedEdges = clip.edges.map((e) => ({
			...e,
			id: `${idMap[e.source]}->${idMap[e.target]}-${Math.random()
				.toString(36)
				.slice(2, 7)}`,
			source: idMap[e.source],
			target: idMap[e.target],
			data: { setEdges },
		}));
		setNodes((nds) =>
			nds.map((n) => ({ ...n, selected: false })).concat(pastedNodes)
		);
		if (pastedEdges.length) setEdges((eds) => eds.concat(pastedEdges));
		return true;
	};

	useEffect(() => {
		const handler = (e) => {
			if (!(e.ctrlKey || e.metaKey)) return;
			const key = (e.key || "").toLowerCase();
			if (key !== "c" && key !== "v") return;
			// não sequestrar copiar/colar de texto nem com o modal aberto
			if (modalOpenRef.current || isTextEntryTarget(document.activeElement)) {
				return;
			}
			if (key === "c") {
				if (copySelection()) e.preventDefault();
			} else if (key === "v") {
				if (pasteSelection()) e.preventDefault();
			}
		};
		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			{loading && <FullScreenLoader />}
			<ToastContainer />
			<LayoutFixModal
				open={layoutFixOpen}
				onApply={() => {
					spreadNodes();
					setLayoutFixOpen(false);
				}}
				onClose={() => setLayoutFixOpen(false)}
			/>
			<SettingsContainer
				open={isSettingsModalOpen}
				handleSettings={handleSettings}
				botId={botId}
				sectors={sectors}
				operators={operators}
				settings={botSettings}
			/>
			<div
				className="dndflow has-topbar"
				style={{ width: "100vw", height: "100vh" }}
			>
				<div className="builder-topbar">
					<span className="builder-topbar__name">{botName}</span>
					<NodeSearch nodes={nodes} onSelect={focusNodeById} inline />
					<ShortcutsHint />
					<WhatsNew />
					<div className="builder-topbar__spacer" />
				</div>
				<ReactFlowProvider>
					<div className="reactflow-wrapper" ref={reactFlowWrapper}>
						<ReactFlow
							nodes={nodes}
							edges={displayEdges}
							multiSelectionKeyCode="Shift"
							onNodesChange={onNodesChange}
							onEdgesChange={onEdgesChange}
							onNodeMouseEnter={onNodeMouseEnter}
							onNodeMouseLeave={onNodeMouseLeave}
							onConnect={onConnect}
							onConnectEnd={() => {
								canOpenModal = false;
								setTimeout(() => {
									canOpenModal = true;
								}, 0);
							}}
							onEdgeDoubleClick={handleKeyDown}
							onNodeDragStart={() => takeSnapshot()}
							onEdgeMouseEnter={onEdgeMouseEnter}
							onEdgeMouseLeave={onEdgeMouseLeave}
							onEdgeUpdateEnd={onEdgeUpdateEnd}
							onInit={(inst) => {
								setReactFlowInstance(inst);
								reactFlowInstanceRef.current = inst;
							}}
							onDrop={onDrop}
							edgeTypes={edgeTypes}
							onDragOver={onDragOver}
							fitView
							attributionPosition="bottom-left"
							nodeTypes={nodeTypes}
							deleteKeyCode={null}
							onNodeClick={(e, node, ...rest) => {
								if (["createupdatecontact", "end", "start"].includes(node.type))
									return;
								if (e.target.classList.contains("deleteNode")) return;
								if (e.target.classList.contains("editNode")) return;
								if (e.target.classList.contains("copy-ref")) return;

								if (canOpenModal) openModalEdit(node);
							}}
						>
							<Background
								variant="dots"
								gap={22}
								size={2.2}
								color={canvasDark ? "#565c7d" : "#c4c5cf"}
								style={{
									backgroundColor: canvasDark ? "#454b6b" : "#eef0f3",
								}}
							/>
							<MiniMap
								nodeColor={"#c9c9cc"}
								maskColor={"rgba(18,18,18,0.08)"}
								nodeStrokeWidth={3}
								pannable
								zoomable={true}
								zoomStep={1}
								ariaLabel={`MiniMap ${botName}`}
							/>
							<Controls showInteractive={false}>
								<ControlButton
									onClick={toggleCanvasTheme}
									title={
										canvasDark
											? "Fundo claro"
											: "Fundo escuro"
									}
								>
									{canvasDark ? <FaSun /> : <FaMoon />}
								</ControlButton>
								<ControlButton
									onClick={() => spreadNodes()}
									title="Espalhar nós (abre espaço mantendo o arranjo)"
								>
									<FaExpandArrowsAlt />
								</ControlButton>
								{/* "Auto-organizar" oculto por enquanto (a pedido) — reativar com a função e o import FaSitemap
								<ControlButton
									onClick={autoLayoutNodes}
									title="Auto-organizar (re-desenha o fluxo por camadas)"
								>
									<FaSitemap />
								</ControlButton>
								*/}
							</Controls>
						</ReactFlow>
					</div>
					<Sidebar
						botId={botId}
						variables={variables}
						setVariables={setVariables}
						nodes={nodes}
						getVars={getVars}
						handleModalVariable={handleModalVariable}
						contactFields={contactFields}
						handleEditVar={handleEditVar}
						handleEditVariable={handleEditVariable}
						editOpen={editOpen}
					>
						<div className="bot-actions">
							<div className={`actions-menu ${actionsMenuOpen ? "open" : ""}`}>
								<button
									type="button"
									className="action-button action-button-icon settings"
									title="Mais ações"
									onClick={() => setActionsMenuOpen((o) => !o)}
								>
									<MdMoreVert />
								</button>
								{actionsMenuOpen && (
									<div className="actions-menu-items">
										<button
											type="button"
											className="action-button action-menu-item analytics"
											title="Analytics do fluxo"
											onClick={async (e) => {
												setActionsMenuOpen(false);
												// Fluxo ainda não carregado = nada editado: só navega.
												// Se o save falhar, fica no builder para não perder edições.
												if (flowLoaded && !(await saveBot(e))) return;
												goToAnalytics();
											}}
										>
											<MdBarChart />
											<span>Estatísticas</span>
										</button>
										<button
											type="button"
											className="action-button action-menu-item settings"
											title="Configurações gerais"
											onClick={() => {
												setActionsMenuOpen(false);
												handleSettings();
											}}
										>
											<MdSettings />
											<span>Configurações</span>
										</button>
									</div>
								)}
							</div>
							<SaveButton
								saveBot={saveBot}
								disabled={!flowLoaded}
							></SaveButton>
							{isRvopsFrame && (
								<button
									type="button"
									className="action-button action-button-icon close-flow"
									title="Fechar fluxo"
									onClick={handleCloseFlow}
								>
									<MdClose />
								</button>
							)}
						</div>
					</Sidebar>

					<Modal
						botId={botId}
						nodeToEdit={nodeToEdit}
						active={stateModalEdit}
						nodes={nodes}
						edges={edges}
						setEdges={setEdges}
						handleModalEdit={handleModalEdit}
						saveNode={saveNode}
						variables={variables}
						setVariables={setVariables}
						owners={owners}
						customDealFields={customDealFields}
						pipelines={dealPipelines}
						customTicketFields={customTicketFields}
						ticketPipelines={ticketPipelines}
						operators={operators}
						agents={agents}
						handleModalVariable={handleModalVariable}
						sectors={sectors}
						tags={chatTags}
						contactFields={contactFields}
						contactConditions={contactConditions}
					></Modal>

					<ModalVariable
						active={modalActive}
						handleModalVariable={handleModalVariable}
						saveVar={saveVar}
						nameVar={nameVar}
						setNameVar={setNameVar}
						contactFields={contactFields}
						setElozProperty={setElozProperty}
						elozProperty={elozProperty}
						account={account}
					></ModalVariable>
				</ReactFlowProvider>
			</div>
		</>
	);
};

export default Flow;
